import { Box, useTheme } from '@material-ui/core';
import { Agent, Channel, MerchantStructure } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { useListPagination } from '@/components/modules/MerchantDetail/MerchantStructure/components/_shared/hooks/useListPagination';
import { agentSearchTermMatchMapper } from '@/components/modules/MerchantDetail/MerchantStructure/components/_shared/maps/agentSearchTermMatchMapper';
import { Font } from '@/components/shared/components/Font';
import { IconButton } from '@/components/shared/components/IconButton';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

import { AgentList } from '../AgentList';

interface AgentListWithPaginationProps {
  agentSearchTerm: string;
  selectedChannel?: Channel;
  merchantStructure: MerchantStructure;
  agentFoundBySearch?: Agent;
  editModeAllowed?: boolean;
  isAgentRedirectAllowed?: boolean;
}

export const AgentListWithPagination = ({
  agentSearchTerm,
  selectedChannel,
  merchantStructure,
  agentFoundBySearch,
  editModeAllowed,
  isAgentRedirectAllowed,
}: AgentListWithPaginationProps) => {
  const { palette } = useTheme();
  const { getTextIdTranslation } = useTranslation();

  const effectiveAgents = useMemo(() => {
    const channelAgents = selectedChannel?.agents ?? [];
    if (!agentSearchTerm) return channelAgents;
    return channelAgents.filter((agent) => agentSearchTermMatchMapper(agent, agentSearchTerm));
  }, [selectedChannel?.agents, agentSearchTerm]);

  const agentFoundIndex = agentFoundBySearch
    ? effectiveAgents.findIndex((a) => a.id === agentFoundBySearch.id)
    : undefined;

  const {
    pagedItems: agents,
    page,
    rowsPerPage,
    onPrev,
    onNext,
    disablePrev,
    disableNext,
    itemsLength,
  } = useListPagination({
    items: effectiveAgents,
    rowsPerPage: 10,
    selectedIndex: agentFoundIndex !== undefined && agentFoundIndex >= 0 ? agentFoundIndex : undefined,
  });

  return (
    <>
      <Box className={'agents-container'} p="2px">
        {itemsLength === 0 ? (
          agentSearchTerm ? (
            <PendingDataWarning tId={'NO_AGENTS_FOUND_BY_SEARCH_TERM'} />
          ) : (
            <PendingDataWarning tId={'THERE_ARE_NO_AGENTS_ON_THIS_CHANNEL_YET'} />
          )
        ) : (
          <AgentList
            agentSearchTerm={agentSearchTerm}
            agents={agents}
            selectedChannel={selectedChannel}
            merchantStructure={merchantStructure}
            agentFoundBySearch={agentFoundBySearch}
            editModeAllowed={editModeAllowed}
            isAgentRedirectAllowed={isAgentRedirectAllowed}
          />
        )}
      </Box>
      {!!itemsLength && (
        <Box className={'agents-pagination'} px={1} style={{ paddingBottom: 4 }}>
          <Font variant="caption" style={{ color: palette.text.secondary }}>
            {`${(page - 1) * rowsPerPage + 1}-${
              itemsLength && page * rowsPerPage > itemsLength ? itemsLength : page * rowsPerPage
            } ${getTextIdTranslation('OF')} ${itemsLength}`}
          </Font>
          <IconButton icon="chevron_left" size="small" onClick={onPrev} disabled={disablePrev} />
          <IconButton icon="chevron_right" size="small" onClick={onNext} disabled={disableNext} />
        </Box>
      )}
    </>
  );
};
